import React, {
    useEffect,
    useState,
} from 'react';

import classNames from 'classnames';
import {
    Button,
    ToggleButtonGroup,
} from 'react-aria-components';

import {
    InfoIcon,
    Text,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import Events from '../events/SearchResultEvents';
import Fare from './Fare/Fare';
import InfoModal from './InfoModal';

import styles from './FareClass.scss';

import type { FareClassType, LiveCheckIds } from './types';
import type { TFunction } from 'i18next';

interface FareClassProps {
    fareClasses: FareClassType[];
    forceModalClose: boolean;
    liveCheckIds: LiveCheckIds;
    performLiveCheck: (tripIds: string[], params: {
        affiliateId?: string;
        seats: number;
        trackingId?: string;
        tripIds: string[];
        wheelchairs: number;
    }) => void;
    seats: number;
    selectedFareCode: string;
    setFareClass: (code: string) => void;
    t: TFunction;
    tripId: string;
    updateTrip: (tripId: string, params: { selectedFare: string }) => void;
    wheelchairs: number;
}

function FareClass({
    fareClasses = [],
    forceModalClose = false,
    liveCheckIds,
    performLiveCheck,
    seats,
    selectedFareCode,
    setFareClass,
    t,
    tripId,
    updateTrip,
    wheelchairs,
}: FareClassProps) {
    const defaultFare = fareClasses[0];
    const selectedFare = fareClasses.find((fare) => fare.code === selectedFareCode) || defaultFare;
    const defaultFareCode = selectedFareCode || defaultFare?.code;
    const [showInfoModal, setShowInfoModal] = useState(false);

    const handleLiveCheck = (fareCode: string) => {
        const {
            allTripIds,
            affiliateId,
            trackingId,
        } = liveCheckIds;

        updateTrip?.(tripId, { selectedFare: fareCode });

        performLiveCheck?.(allTripIds, {
            affiliateId,
            seats,
            trackingId,
            tripIds: allTripIds,
            wheelchairs,
        });
    };

    const onChange = (fareCode: string, type: string) => {
        setFareClass?.(fareCode);
        handleLiveCheck(fareCode);
        Events.fareClassSelected({ fareClassCode: fareCode, fareClassType: type, tripId });
    };

    useEffect(() => {
        if (forceModalClose && showInfoModal) {
            setShowInfoModal(false);
        }
    }, [forceModalClose, showInfoModal]);

    if (!fareClasses.length) {
        return null;
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.titleContainer}>
                    <Text
                        component="h4"
                        variant="text-xl"
                        bold
                    >
                        {t('fare-selection')}
                    </Text>
                    <Button
                        className={styles.fareConditions}
                        onPress={() => setShowInfoModal(!showInfoModal)}
                    >
                        <Text variant="text-s">
                            {t('fare-conditions')}
                        </Text>
                        <InfoIcon className={styles.icon} />
                    </Button>
                </div>
                <ToggleButtonGroup
                    className={classNames(styles.faresContainer, {
                        [styles.halves]: [2, 4].includes(fareClasses.length),
                        [styles.thirds]: ![2, 4].includes(fareClasses.length),
                    })}
                    defaultSelectedKeys={[defaultFareCode]}
                    disallowEmptySelection
                >
                    {fareClasses.map((fare) => (
                        <Fare
                            code={fare.code}
                            details={fare?.description?.fare_details}
                            disabled={!fare.quantity}
                            farePrice={fare.price}
                            forceModalClose={forceModalClose}
                            key={fare.code}
                            onChange={onChange}
                            selectedFarePrice={selectedFare?.price}
                            type={fare.wanderu_type}
                        />
                    ))}
                </ToggleButtonGroup>
            </div>
            {showInfoModal && (
                <InfoModal
                    fareClasses={fareClasses}
                    setShowInfoModal={setShowInfoModal}
                    showInfoModal={showInfoModal}
                    t={t}
                />
            )}
        </>
    );
}

export {
    FareClass,
};

export default localize(FareClass, ['t'], 'baseApp');
